import React, { useEffect, useRef, useState } from 'react';
import { Editor, useEditor, EditorContent } from '@tiptap/react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { spaces } from '../../../assets/styles/themes/tokens';
import ContextMenu from './ContextMenu';
import { TipTapExtensionKit } from '../../../utils/tipTapEditor';
import { useTranslation } from 'react-i18next';
import LinkMenuWrapper from './LinkMenu/LinkMenuWrapper';
import GenerateWorksheetDialog from '../GenerateWorksheetDialog';
import { useSelector } from 'react-redux';
import { getLernsetById } from '../../../reducers/lernsets.selectors';
import useRouter from '../../../utils/useRouter';
import { GenerateWorksheetPlacement } from '../GenerateWorksheetDialog/GenerateWorksheetDialog';

interface StyledEditorContentProps {
  editoremptyplaceholder: string;
  lineemptyplaceholder: string;
}

const StyledEditorContent = 
  styled(EditorContent)<StyledEditorContentProps>(({ editoremptyplaceholder, lineemptyplaceholder }) => ({
    '& .ProseMirror:focus': { outline: 'none' },
    '& .tiptap p.is-editor-empty:first-of-type::before, & .tiptap p.is-empty::before': {
      opacity: 0.5,
      float: 'left',
      height: 0,
      pointerEvents: 'none',
    },
    '& .tiptap p.is-editor-empty:first-of-type::before': {
      content: `"${editoremptyplaceholder}"`,
    },
    '& .tiptap p.is-empty::before': {
      content: `"${lineemptyplaceholder}"`,
    },
    '& .tiptap blockquote': {
      borderLeft: '3px solid #ddd',
      margin: '1.5rem 0',
      paddingLeft: '1rem',
    },
  }));

interface OwnProps {
  id: string;
  content: string;
  handleUpdateContent: (currentContent: string, newContent: string) => void;
  isEditable?: boolean;
}

const richTextSaveTimeoutMs = 2000;

const RichTextEditor: React.FC<OwnProps> = (props) => {
  const { id, content, handleUpdateContent, isEditable = false } = props;
  const { match: { params: { lernsetId } } } = useRouter();
  const { t } = useTranslation();
  const currentContent = useRef(content);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isGenerateWorksheetDialogOpen, setIsGenerateWorksheetDialogOpen] = useState(false);
  const lernset = useSelector(state => getLernsetById(state, lernsetId));
  const openGenerateWorksheetModal = () => setIsGenerateWorksheetDialogOpen(true);

  const config = {
    ai: {
      openGenerateWorksheetModal,
    }
  };

  const editor: Editor | null = useEditor(
    {
      editable: isEditable,
      content,
      extensions: TipTapExtensionKit(config),
      editorProps: {
        attributes: {
          id,
          autocomplete: 'off',
          autocorrect: 'off',
          autocapitalize: 'off',
        },
      },
    },
    [],
  );

  const handleWorksheetInsert = (worksheetHtml: string) => {
    if (editor) {
      const position = editor.state.selection.from;
      editor.chain().focus().insertContentAt(position, worksheetHtml).run();
      const newContent = editor.getHTML();
      handleUpdateContent(currentContent.current, newContent);
      currentContent.current = newContent;
    }
  }; 
  
  useEffect(() => {
    if (!editor) return;

    editor.on('update', () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        const newContent = editor.getHTML();
        if (newContent !== currentContent.current) {
          handleUpdateContent(currentContent.current, newContent);
          currentContent.current = newContent;
        }
      }, richTextSaveTimeoutMs);
    });

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [editor]);

  if (!editor) return null; 

  return (
    <Box 
      data-testid='text-editor-container'
      sx={{
        padding: spaces.lg,
      }}>
      <StyledEditorContent 
        editor={editor}
        editoremptyplaceholder={t('Click here to write...')}
        lineemptyplaceholder={t('Type / to browse options')}
      />
      <ContextMenu editor={editor}/>
      <LinkMenuWrapper 
        editor={editor}
      />
      <GenerateWorksheetDialog
        isOpen={isGenerateWorksheetDialogOpen}
        onCloseModal={() => setIsGenerateWorksheetDialogOpen(false)}
        onGenerateWorksheetInsert={handleWorksheetInsert}
        lernset={lernset}
        placement={GenerateWorksheetPlacement.RichTextEditor}
      />
    </Box>
  );
};

export default RichTextEditor;
